import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";

import { useAppSelector } from "../../redux/hooks";
import { setErrors } from "../../redux/reducers/errorReducers";
import { selectModules } from "../../redux/selectors/modules";
import discoveryPanelImage from "../../assets/images/background/Onboard.png";
import trellisLogomark from "../../assets/images/logo/Trellis_Logomark_color.png";
import bgImage from "../../assets/images/background/purple_gradient.png";
import chromeStoreLogo from "../../assets/images/logo/chrome_store.png";
import { SubscriptionCard } from "../../modules/billing/components/SubscriptionCard";
import { Switch } from "../../components/fields/base/Switch";
import ConfirmationModal from "../../components/core/blocks/ConfirmationModal";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import { SUBSCRIPTION_PLANS_UPPERCASE } from "../../utils/onboardingConstants";
import checkModule from "../../utils/checkModule";
import { useAmazonOnboardingStatus } from "../../modules/onboarding/amazon/useAmazonOnboardingStatus";
import { TextField } from "../../components/fields/base/TextField";
import { css } from "../../../styled-system/css";
import { Grid, HStack, VStack } from "../../../styled-system/jsx";

const URL_PREFIX = getURLPrefix();

const subscriptionPlans = SUBSCRIPTION_PLANS_UPPERCASE;

const BlogCard = ({ title, link }) => (
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    className={css({ textDecoration: "none", display: "block", width: "100%" })}
  >
    <div
      className={css({
        width: "100%",
        padding: "medium",
        background: "rgba(255, 255, 255, 0.15)",
        backdropFilter: "blur(8px)",
        borderRadius: "standard",
        fontSize: "medium",
        fontWeight: 600,
        color: "white",
        textAlign: "center",
        border: "1px solid rgba(255, 255, 255, 0.25)",
        transition: "all 0.2s ease",
        _hover: {
          background: "rgba(255, 255, 255, 0.25)",
          transform: "translateY(-2px)",
        },
      })}
    >
      {title}
    </div>
  </a>
);

const ANNUAL_DISCOUNT_PERCENTAGE = 10;

const getCardData = (discoveryType, modules, t) => {
  if (["advertising", "purchase-behavior", "search-analytics"].includes(discoveryType)) {
    return [
      {
        planId: "Extension",
        planName: t(($) => $.onboarding["Chrome Extension"]),
        monthlySubscriptionPrice: t(($) => $.onboarding["Free"]),
        annualSubscriptionPrice: t(($) => $.onboarding["Free"]),
        subscriptionFeatures: "chrome-extension",
        onCardClick: () => {},
        core: true,
      },
      {
        planId: "CORE_CUSTOM",
        planName: t(($) => $.onboarding["Custom"]),
        monthlySubscriptionPrice: t(($) => $.onboarding["TBD"]),
        annualSubscriptionPrice: t(($) => $.onboarding["TBD"]),
        subscriptionFeatures: (
          <>
            <span>{t(($) => $.onboarding["Placement Advertising"])}</span>
            <br />
            <span>{t(($) => $.onboarding["Product Content"])}</span>
            <br />
            <span>{t(($) => $.onboarding["Dynamic Pricing"])}</span>
            <br />
            <span>{t(($) => $.onboarding["Promotions & Deals"])}</span>
          </>
        ),
        core: true,
      },
    ];
  }

  if (discoveryType === "content") {
    return [
      {
        planId: "STANDARD_CONTENT",
        planName: t(($) => $.onboarding["Standard"]),
        monthlySubscriptionPrice: "$49.99",
        annualSubscriptionPrice: "$44.99",
        subscriptionFeatures: <>{t(($) => $.onboarding["All Products"])}</>,
        annualSales: "",
      },
    ];
  }

  const cards = [
    {
      planId: "BUSINESS_PRICING",
      planName: t(($) => $.onboarding["Starter"]),
      monthlySubscriptionPrice: "$299",
      annualSubscriptionPrice: "$269",
      subscriptionFeatures: <>{t(($) => $.onboarding["40 Optimized Prices"])}</>,
      annualSales: "$120k to $360k",
    },
    {
      planId: "PROFESSIONAL_PRICING",
      planName: t(($) => $.onboarding["Premium"]),
      monthlySubscriptionPrice: "$499",
      annualSubscriptionPrice: "$449",
      subscriptionFeatures: <>{t(($) => $.onboarding["100 Optimized Prices"])}</>,
      annualSales: "$360k to $1M",
    },
    {
      planId: "ENTERPRISE_PRICING",
      planName: t(($) => $.onboarding["Enterprise"]),
      monthlySubscriptionPrice: "$999",
      annualSubscriptionPrice: "$899",
      subscriptionFeatures: <>{t(($) => $.onboarding["Unlimited Optimized Prices"])}</>,
      annualSales: "$1M to $3M",
    },
  ];

  if (checkModule(modules, "CUSTOM_TIER")) {
    cards.push({
      planId: "CORPORATE_PRICING",
      planName: t(($) => $.onboarding["Custom"]),
      monthlySubscriptionPrice: t(($) => $.onboarding["TBD"]),
      annualSubscriptionPrice: t(($) => $.onboarding["TBD"]),
      subscriptionFeatures: (
        <p className={css({ fontSize: "14px" })}>{t(($) => $.onboarding["Features to be determined."])}</p>
      ),
      annualSales: t(($) => $.onboarding["Above $3M"]),
    });
  }

  return cards;
};

const getSearchAnalyticsCards = (t) => [
  {
    planId: "FREE_ANALYTICS",
    planName: t(($) => $.onboarding["Free"]),
    monthlySubscriptionPrice: t(($) => $.onboarding["FREE"]),
    annualSubscriptionPrice: t(($) => $.onboarding["FREE"]),
    subscriptionFeatures: <>{t(($) => $.onboarding["2 active shelves"])}</>,
    annualSales: t(($) => $.onboarding["Below $120k"]),
  },
  {
    planId: "ENTREPRENEUR_ANALYTICS",
    planName: t(($) => $.onboarding["Essentials"]),
    monthlySubscriptionPrice: "$49",
    annualSubscriptionPrice: "$44",
    subscriptionFeatures: <>{t(($) => $.onboarding["5 active shelves"])}</>,
    annualSales: t(($) => $.onboarding["Up to $120k"]),
  },
  {
    planId: "PROFESSIONAL_ANALYTICS",
    planName: t(($) => $.onboarding["Premium"]),
    monthlySubscriptionPrice: "$99",
    annualSubscriptionPrice: "$89",
    subscriptionFeatures: <>{t(($) => $.onboarding["15 active shelves"])}</>,
    annualSales: "$360k to $1M",
  },
  {
    planId: "ENTERPRISE_ANALYTICS",
    planName: t(($) => $.onboarding["Enterprise"]),
    monthlySubscriptionPrice: "$199",
    annualSubscriptionPrice: "$179",
    subscriptionFeatures: <>{t(($) => $.onboarding["40 active shelves"])}</>,
    annualSales: t(($) => $.onboarding["$1M and above"]),
  },
];

/** @param {{ discoveryType?: string, dashboardType?: string }} props */
const PaymentDiscovery = ({ discoveryType, dashboardType }) => {
  const { t } = useTranslation(["onboarding"]);
  const navigate = useNavigate();
  const auth = useAppSelector((state) => state.auth);
  const user = useAppSelector((state) => state.user);
  const modules = useAppSelector(selectModules);
  const { data: amz_onboard } = useAmazonOnboardingStatus();
  const [subscriptionPlanName, setSubscriptionPlanName] = useState("");
  const [monthlyChecked, setMonthlyChecked] = useState(true);
  const [promoCode, setPromoCode] = useState("");

  const hideBillingPeriodSwitch =
    (user.tier?.indexOf("MONTHLY") > -1 &&
      user.pricing_tier === "unknown" &&
      amz_onboard?.payment_discovery !== "pending") ||
    (user.pricing_tier?.indexOf("MONTHLY") > -1 &&
      user.tier === "unknown" &&
      amz_onboard?.payment_discovery !== "pending") ||
    (user.tier?.indexOf("ANNUAL") > -1 && user.pricing_tier === "unknown") ||
    (user.pricing_tier?.indexOf("ANNUAL") > -1 && user.tier === "unknown") ||
    (user.analytics_tier === "unknown" && (user.tier !== "unknown" || user.pricing_tier !== "unknown")) ||
    amz_onboard?.org_status === "active";

  useEffect(() => {
    getSubscriptionPlan();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (user.tier?.indexOf("ANNUAL") > -1 && user.pricing_tier === "unknown") ||
      (user.pricing_tier?.indexOf("ANNUAL") > -1 && user.tier === "unknown" && monthlyChecked)
    ) {
      setMonthlyChecked(false);
    }
  }, [user, monthlyChecked]);

  const subscriptionPlan = subscriptionPlanName ? subscriptionPlans[subscriptionPlanName] : null;

  const getSubscriptionPlan = () => {
    const AuthHeader = "Bearer ".concat(auth.tokens.access_token);

    axios
      .get(URL_PREFIX + "/api/aboutyourcompany", {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((result) => {
        if (result.data.tier !== "") {
          const plan = result.data.tier.split("__");

          setSubscriptionPlanName(plan[0]);

          if (plan[1] === "ANNUAL") {
            setMonthlyChecked(false);
          } else if (plan[1] === "MONTHLY") {
            setMonthlyChecked(true);
          }
        } else {
          setSubscriptionPlanName("ENTREPRENEUR");
        }
      })
      .catch(() => {
        setErrors({
          error: {
            title: t(($) => $.onboarding["Sorry, something went wrong while fetching your subscription plan"]),
            message: t(($) => $.onboarding["Please try refreshing/reloading the page."]),
          },
        });
      });
  };

  const onCardClick = (e, planId) => {
    e.stopPropagation();
    e.preventDefault();

    setSubscriptionPlanName(planId);
  };

  if (!discoveryType) {
    discoveryType = "advertising";
  }

  const cards =
    discoveryType === "search-analytics" ? getSearchAnalyticsCards(t)
    : discoveryType === "purchase-behavior" ?
      [
        {
          planId: "STANDARD_PURCHASE_BEHAVIOR",
          planName: t(($) => $.onboarding["Standard"]),
          monthlySubscriptionPrice: "$49.99",
          annualSubscriptionPrice: "$44.99",
          subscriptionFeatures: <>{t(($) => $.onboarding["All Products"])}</>,
          annualSales: "",
        },
      ]
    : getCardData(discoveryType, modules, t);

  return (
    <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 480px" }} translate="no" className={css({ minHeight: "100vh" })}>
      <VStack
        gap="large"
        alignItems="stretch"
        className={css({
          backgroundColor: "white",
          padding: { base: "medium", md: "xlarge", lg: "xxlarge" },
          overflowX: "hidden",
        })}
      >
        <VStack gap="medium" alignItems="stretch" className={css({ maxWidth: "1100px", mx: "auto", width: "100%" })}>
          <h2
            className={css({
              textAlign: { base: "center", lg: "left" },
              color: "text.heading",
              fontSize: "26px",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              marginTop: discoveryType === "pricing" ? "xxlarge" : "xlarge",
            })}
          >
            {t(($) => $.onboarding["Explore all features of our merchandising platform!"])}
          </h2>

          <HStack gap="medium" justify="space-between" flexWrap="wrap" alignItems="center">
            <TextField
              value={promoCode}
              onChange={setPromoCode}
              placeholder={t(($) => $.onboarding["Have a promo code? Enter it here."])}
              css={{ flex: 1, minWidth: "200px", maxWidth: "300px" }}
            />

            {!hideBillingPeriodSwitch && (
              <HStack gap="small" alignItems="center">
                {discoveryType === "advertising" && (
                  <span className={css({ color: "darkgrey", fontSize: "14px", fontWeight: 500 })}>
                    {t(($) => $.onboarding["Save 20% on an annual subscription."])}
                  </span>
                )}
                <span className={css({ fontSize: "14px" })}>
                  {monthlyChecked ? t(($) => $.onboarding["Monthly"]) : t(($) => $.onboarding["Annual"])}
                </span>
                <Switch
                  isSelected={!monthlyChecked}
                  onChange={() => setMonthlyChecked(!monthlyChecked)}
                  isDisabled={hideBillingPeriodSwitch}
                />
              </HStack>
            )}
          </HStack>

          <VStack gap="small" alignItems="stretch">
            {cards.map((card) => (
              <SubscriptionCard
                key={card.planId}
                planId={card.planId}
                planName={card.planName}
                monthlySubscriptionPrice={card.monthlySubscriptionPrice}
                annualSubscriptionPrice={card.annualSubscriptionPrice}
                monthlyChecked={monthlyChecked}
                subscriptionFeatures={
                  card.subscriptionFeatures === "chrome-extension" ?
                    <div className={css({ display: "flex", gap: "medium", alignItems: "center" })}>
                      <div>
                        <span>{t(($) => $.onboarding["Free Google Chrome Seller Extension"])}</span>
                        <br />
                      </div>
                      <div>
                        <a
                          href="https://chromewebstore.google.com/detail/trellis-seller-extension/mfnnikclhbphfbkojdfoeccbfpjcbbdl?utm_source=portal&utm_medium=web&utm_campaign=trellis_banner"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none", fontWeight: "bold", color: "white" }}
                        >
                          <img
                            src={chromeStoreLogo}
                            alt={t(($) => $.onboarding["Google Chrome Store"])}
                            style={{ height: "40px", backgroundColor: "white" }}
                          />
                        </a>
                      </div>
                    </div>
                  : card.subscriptionFeatures
                }
                selectedSubscription={subscriptionPlanName}
                onCardClick={card.onCardClick ?? ((e, planName) => onCardClick(e, planName))}
                annualDiscountPercentage={ANNUAL_DISCOUNT_PERCENTAGE}
                annualSales={card.annualSales}
                core={card.core}
              />
            ))}
          </VStack>

          <HStack gap="medium" flexWrap="wrap" alignItems="center" className={css({ marginTop: "medium" })}>
            <p className={css({ fontSize: "16px", fontWeight: 500, flex: 1, color: "text.secondary" })}>
              {t(
                ($) =>
                  $.onboarding[
                    "Want to find out more about our managed service offerings, advanced analytics features, or are you having trouble choosing a plan?"
                  ],
              )}
            </p>
            <a
              href="https://gotrellis.com/book-a-demo/"
              target="_blank"
              rel="noreferrer"
              className={css({
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "large",
                color: "fuchsia",
                transition: "color 0.15s ease",
                _hover: { color: "mauve", textDecoration: "underline" },
              })}
            >
              {t(($) => $.onboarding["Click here to talk to Customer Success."])}
            </a>
          </HStack>

          {amz_onboard?.org_status !== "active" && (
            <div className={css({ display: "flex", justifyContent: "center", marginTop: "large" })}>
              <ConfirmationModal
                title={t(($) => $.onboarding["Confirm Subscription"])}
                modalButtonText={t(($) => $.onboarding["Update New Subscription"])}
                confirmButtonText={t(($) => $.onboarding["Confirm"])}
                confirmationMessage={
                  <p style={{ fontSize: "1.7rem" }}>
                    {t(($) => $.onboarding["Are you sure you want to sign up for the {plan} {tier} tier?"], {
                      plan: subscriptionPlan?.displayName ? t(subscriptionPlan.displayName) : "",
                      tier:
                        discoveryType === "search-analytics" ? t(($) => $.onboarding["Search Analytics"])
                        : discoveryType === "content" ? t(($) => $.onboarding["Product Content"])
                        : discoveryType === "pricing" ? t(($) => $.onboarding["Dynamic Pricing"])
                        : t(($) => $.onboarding["Advertising"]),
                    })}
                  </p>
                }
                disabled={!subscriptionPlan}
                onConfirm={() => navigate({ to: "/user/payment" })}
                modelButtonStyle={{
                  marginBottom:
                    discoveryType === "pricing" || discoveryType === "search-analytics" ? "32px" : "inherit",
                }}
              />
            </div>
          )}
        </VStack>
      </VStack>

      <div
        className={css({
          display: { base: "none", md: "block" },
          position: "sticky",
          top: 0,
          alignSelf: "start",
          height: "100vh",
          overflow: "hidden",
        })}
      >
        <div
          className={css({
            width: "100%",
            height: "100%",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          })}
          style={{ backgroundImage: `url(${bgImage})` }}
        >
          <VStack
            gap="medium"
            alignItems="center"
            justifyContent="center"
            className={css({
              height: "100%",
              paddingX: "large",
              paddingY: "large",
            })}
            translate="yes"
          >
            <img
              src={discoveryPanelImage}
              alt={t(($) => $.onboarding["A lightbulb with an arrow."])}
              className={css({ width: "100%", maxWidth: "260px" })}
            />

            <h2
              className={css({
                textAlign: "center",
                color: "white",
                fontSize: "26px",
                fontWeight: 800,
                lineHeight: 1.1,
              })}
            >
              {dashboardType === "advertising" ?
                user.tier !== "unknown" || user.pricing_tier !== "unknown" ?
                  t(($) => $.onboarding["Sustainable growth starts here"])
                : t(($) => $.onboarding["What is Advertising Automation?"])
              : dashboardType === "search-analytics" ?
                t(($) => $.onboarding["What is Search Analytics?"])
              : dashboardType === "content" ?
                t(($) => $.onboarding["What is Product Content?"])
              : user.tier !== "unknown" || user.pricing_tier !== "unknown" ?
                t(($) => $.onboarding["Price products with confidence"])
              : t(($) => $.onboarding["What is Dynamic Pricing?"])}
            </h2>

            {user.tier !== "unknown" || user.pricing_tier !== "unknown" ?
              <p
                className={css({
                  textAlign: "center",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 500,
                  padding: "0 medium",
                })}
              >
                {dashboardType === "advertising" ?
                  t(
                    ($) =>
                      $.onboarding[
                        "Our proprietary algorithm auto bids on each keyword using advanced analytics considering consumption trends and competitive analysis."
                      ],
                  )
                : dashboardType === "search-analytics" ?
                  t(
                    ($) =>
                      $.onboarding[
                        "Digital Shelf analytics for eCommerce that transforms the search keyword data into profitable insights. Our platform displays dynamic charts about how your brand ranks and performs against the competition."
                      ],
                  )
                : dashboardType === "content" ?
                  t(
                    ($) =>
                      $.onboarding[
                        "Content scores your products using best practices and traffic and conversion data. Our platform displays dynamic charts about how your brand ranks and performs against the competition."
                      ],
                  )
                : t(
                    ($) =>
                      $.onboarding[
                        "Once you build your price profile, our algorithms will suggest the winning prices set by your goal; maximize sales volume or profit margin. You can also enroll your products for auto-pricing daily."
                      ],
                  )
                }
              </p>
            : dashboardType === "pricing" ?
              <VStack gap="small" width="100%">
                <BlogCard
                  title={t(($) => $.onboarding["Ultimate Intro to Dynamic Pricing"])}
                  link="https://gotrellis.com/blog/ultimate-intro-to-dynamic-pricing/"
                />
                <BlogCard
                  title={t(($) => $.onboarding["Is your Product's Price Too High, Too Low, or Just Right?"])}
                  link="https://gotrellis.com/blog/goldilocks-pricing/"
                />
                <BlogCard
                  title={t(($) => $.onboarding["The Subtle Art of Pricing Amid Inflation"])}
                  link="https://gotrellis.com/blog/pricing-amid-inflation/"
                />
              </VStack>
            : <VStack gap="small" width="100%">
                <BlogCard
                  title={t(($) => $.onboarding["Trellis vs Manual Amazon Ad Campaigns: How to Save Time and Money?"])}
                  link="https://gotrellis.com/blog/trellis-manual-amazon-campaign/"
                />
                <BlogCard
                  title={t(($) => $.onboarding["Case Study: +147% sales using PPC Automation"])}
                  link="https://gotrellis.com/blog/case-study-ppc-automation/"
                />
                <BlogCard
                  title={t(($) => $.onboarding["Case Study: World Products Saves 1,650 Hours Using Trellis"])}
                  link="https://gotrellis.com/blog/case-study-world-products-agency/"
                />
              </VStack>
            }

            <HStack gap="small" alignItems="center">
              <img src={trellisLogomark} alt="Trellis" width="24px" />
              <span
                className={css({
                  color: "white",
                  fontSize: "22px",
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                })}
              >
                trellis
              </span>
            </HStack>
          </VStack>
        </div>
      </div>
    </Grid>
  );
};

export default PaymentDiscovery;
